// Metric commands (CFO plan 4.4). Pure; see tasks.js for the ctx shape. Extra ctx: metrics (business defs),
// personalMetrics (yours), metricValues, personalMetricValues, uid (else by.uid).
// Business defs live in `metrics/{id}` (owners write), business values in `metricValues/{id}` (yours, or anyone's
// for an owner); personal ones in `personalMetrics` / `personalMetricValues` (opsUsers/{uid}/…, private).
import { set, del } from './writes.js';
import { makeMetric, metricNumber, goalCrossed, VALUE_SOURCES } from '../metrics.js';
import { localDateStr } from '../time.js';

const now = (ctx) => (typeof ctx.now === 'function' ? ctx.now() : ctx.now ?? Date.now());
const DEFS = (personal) => (personal ? 'personalMetrics' : 'metrics');
const VALS = (personal) => (personal ? 'personalMetricValues' : 'metricValues');
const defsOf = (ctx, personal) => (personal ? ctx.personalMetrics : ctx.metrics) || [];
const valsOf = (ctx, personal) => (personal ? ctx.personalMetricValues : ctx.metricValues) || [];
const meOf = (ctx) => ctx.uid || ctx.by?.uid || null;

/** Create or replace a definition. input = { metric, personal } (metric.id kept, else a new one). */
export function saveMetric(ctx, { metric, personal = false }) {
  const m = makeMetric(metric || {});
  if (!m.name) throw new Error('name required');
  const id = metric.id || ctx.newId();
  const at = now(ctx);
  const was = defsOf(ctx, personal).find((x) => x.id === id);
  const doc = { ...m, id, createdAt: was?.createdAt || metric.createdAt || at, updatedAt: at };
  return { writes: [set(`${DEFS(personal)}/${id}`, doc)], metric: doc };
}

/** Delete a definition. A personal one takes its values with it (they mean nothing alone); business values stay. */
export function deleteMetric(ctx, { id, personal = false }) {
  if (!defsOf(ctx, personal).some((x) => x.id === id)) throw new Error(`no metric ${id}`);
  const writes = [del(`${DEFS(personal)}/${id}`)];
  if (personal) for (const v of valsOf(ctx, true)) if (v.metricId === id) writes.push(del(`personalMetricValues/${v.id}`));
  return { writes };
}

/**
 * One value: a tap on a tile (+1), a quantity / note (long-press), a capture on completion, an MCP entry.
 * input = { metricId, value = 1, personal, note, source, taskId, projectId, target, at, uid }.
 * Returns the doc and whether it crossed the goal ('reached' / 'over' / null) for the in-app notice.
 */
export function logMetric(ctx, { metricId, value = 1, personal = false, note = null, source = 'tap', taskId = null, projectId = null, target = null, at = null, uid = null }) {
  const def = defsOf(ctx, personal).find((x) => x.id === metricId);
  if (!def) throw new Error(`no ${personal ? 'personal ' : ''}metric ${metricId}`);
  if (def.source === 'auto') throw new Error(`${def.name} is counted automatically`);
  const n = metricNumber(def, value);
  if (n == null) throw new Error(`${def.name}: a number is needed`);
  const when = Number.isFinite(at) ? at : now(ctx);
  const who = uid || meOf(ctx);
  if (!who) throw new Error('who is it for? (no uid)');
  const id = ctx.newId();
  const tgt = target && target.kind ? { kind: String(target.kind), id: String(target.id ?? '') } : taskId ? { kind: 'task', id: taskId } : projectId ? { kind: 'project', id: projectId } : { kind: 'person', id: who };
  const doc = {
    id, metricId, value: n, at: when, date: localDateStr(when), source: VALUE_SOURCES.includes(source) ? source : 'tap',
    target: tgt, taskId: taskId || null, projectId: projectId || null, uid: who,   // uid = who it counts for (access judges by it; the server stamps ownerUid = the writer)
    ...(String(note || '').trim() ? { note: String(note).trim().slice(0, 500) } : {}),
  };
  const before = valsOf(ctx, personal).filter((v) => v.metricId === metricId && (personal || v.uid === who));
  return { writes: [set(`${VALS(personal)}/${id}`, doc)], value: doc, crossed: goalCrossed(def, before, n, { now: when }) };
}

/** Take a value back (the undo toast, or a wrong entry). */
export function deleteMetricValue(ctx, { id, personal = false }) {
  if (!valsOf(ctx, personal).some((v) => v.id === id)) throw new Error(`no value ${id}`);
  return { writes: [del(`${VALS(personal)}/${id}`)] };
}

/**
 * The values asked for when a task is done (business metrics, source 'task', on the task). Used by completeTask
 * (captures passed in) and by the completion sheet afterwards. input = { task, captures: [{ metricId, value, note? }] }.
 */
export function captureWrites(ctx, { task, captures = [] }) {
  const writes = []; const values = [];
  for (const c of captures || []) {
    if (!c || c.value === null || c.value === undefined || c.value === '') continue;
    const r = logMetric(ctx, { metricId: c.metricId, value: c.value, note: c.note || null, source: 'task', taskId: task.id, projectId: task.projectId || null, at: c.at ?? null });
    writes.push(...r.writes); values.push(r.value);
  }
  return { writes, values };
}
